import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { Switch } from '@/components/ui/switch';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Skeleton } from '@/components/ui/skeleton';
import { Separator } from '@/components/ui/separator';
import { Save, Bell, Shield, Clock, AlertTriangle, ArrowLeft, Settings } from 'lucide-react';
import { checkInOutService } from '@/services/checkinout-service';
import type { CheckInOutSettings } from '@/types/checkinout';
import { toast } from 'sonner';

export default function CheckInOutSettingsPage(): React.ReactElement {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [settings, setSettings] = useState<CheckInOutSettings | null>(null);

  useEffect(() => {
    loadSettings();
  }, []);

  const loadSettings = async () => {
    try {
      setLoading(true);
      const data = await checkInOutService.getSettings();
      setSettings(data);
    } catch (error) {
      console.error('Failed to load settings:', error);
      toast.error('Failed to load settings');
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    if (!settings) return;
    
    try {
      setSaving(true);
      await checkInOutService.updateSettings(settings);
      toast.success('Settings saved successfully');
    } catch (error) {
      console.error('Failed to save settings:', error);
      toast.error('Failed to save settings');
    } finally {
      setSaving(false);
    }
  };

  const updateSetting = <K extends keyof CheckInOutSettings>(
    key: K,
    value: CheckInOutSettings[K]
  ) => {
    if (settings) {
      setSettings({ ...settings, [key]: value });
    }
  };

  if (loading || !settings) {
    return (
      <div className="flex-1 space-y-6 p-6">
        <Skeleton className="h-8 w-64" />
        <Skeleton className="h-96" />
      </div>
    );
  }

  return (
    <div className="flex h-full flex-col">
      {/* Header */}
      <div className="border-b bg-card p-6">
        <div className="flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
          <div>
            <h1 className="text-2xl font-bold tracking-tight flex items-center gap-2">
              <Settings className="h-6 w-6 text-primary" />
              Check-In / Check-Out Settings
            </h1>
            <p className="text-sm text-muted-foreground mt-1">
              Configure check-in/check-out policies and notification settings
            </p>
          </div>
          <div className="flex gap-2">
            <Button variant="outline" onClick={() => navigate('/checkinout')}>
              <ArrowLeft className="mr-2 h-4 w-4" />
              Back
            </Button>
            <Button onClick={handleSave} disabled={saving}>
              <Save className="mr-2 h-4 w-4" />
              {saving ? 'Saving...' : 'Save Changes'}
            </Button>
          </div>
        </div>
      </div>

      <div className="flex-1 space-y-6 p-6">
      <div className="grid gap-6">
        {/* Reminders */}
        <Card>
          <CardHeader>
            <div className="flex items-center gap-2">
              <Bell className="h-5 w-5" />
              <CardTitle>Reminders</CardTitle>
            </div>
            <CardDescription>
              Configure automatic reminders for upcoming returns
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="flex items-center justify-between">
              <div className="space-y-0.5">
                <Label>Enable Reminders</Label>
                <p className="text-sm text-muted-foreground">
                  Send automatic reminders before assets are due
                </p>
              </div>
              <Switch
                checked={settings.reminderEnabled}
                onCheckedChange={(checked) => updateSetting('reminderEnabled', checked)}
              />
            </div>

            {settings.reminderEnabled && (
              <>
                <Separator />
                <div className="grid gap-4 md:grid-cols-2">
                  <div className="space-y-2">
                    <Label>Days Before Due Date</Label>
                    <Input
                      type="number"
                      value={settings.reminderDaysBefore}
                      onChange={(e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => updateSetting('reminderDaysBefore', parseInt(e.target.value))}
                      min={1}
                      max={30}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Reminder Frequency</Label>
                    <Select
                      value={settings.reminderFrequency}
                      onValueChange={(value: any) => updateSetting('reminderFrequency', value)}
                    >
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="daily">Daily</SelectItem>
                        <SelectItem value="every-2-days">Every 2 Days</SelectItem>
                        <SelectItem value="weekly">Weekly</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
              </>
            )}
          </CardContent>
        </Card>

        {/* Grace Period */}
        <Card>
          <CardHeader>
            <div className="flex items-center gap-2">
              <Clock className="h-5 w-5" />
              <CardTitle>Grace Period</CardTitle>
            </div>
            <CardDescription>
              Allow a grace period before marking items as overdue
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="flex items-center justify-between">
              <div className="space-y-0.5">
                <Label>Enable Grace Period</Label>
                <p className="text-sm text-muted-foreground">
                  Give users extra time before escalation
                </p>
              </div>
              <Switch
                checked={settings.gracePeriodEnabled}
                onCheckedChange={(checked) => updateSetting('gracePeriodEnabled', checked)}
              />
            </div>

            {settings.gracePeriodEnabled && (
              <>
                <Separator />
                <div className="space-y-2">
                  <Label>Grace Period (Days)</Label>
                  <Input
                    type="number"
                    value={settings.gracePeriodDays}
                    onChange={(e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => updateSetting('gracePeriodDays', parseInt(e.target.value))}
                    min={0}
                    max={7}
                  />
                  <p className="text-sm text-muted-foreground">
                    Number of days after due date before marking as overdue
                  </p>
                </div>
              </>
            )}
          </CardContent>
        </Card>

        {/* Escalation */}
        <Card>
          <CardHeader>
            <div className="flex items-center gap-2">
              <AlertTriangle className="h-5 w-5" />
              <CardTitle>Escalation</CardTitle>
            </div>
            <CardDescription>
              Automatically escalate overdue items to management
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="flex items-center justify-between">
              <div className="space-y-0.5">
                <Label>Enable Escalation</Label>
                <p className="text-sm text-muted-foreground">
                  Notify managers about overdue items
                </p>
              </div>
              <Switch
                checked={settings.escalationEnabled}
                onCheckedChange={(checked) => updateSetting('escalationEnabled', checked)}
              />
            </div>

            {settings.escalationEnabled && (
              <>
                <Separator />
                <div className="space-y-2">
                  <Label>Escalate After (Days Overdue)</Label>
                  <Input
                    type="number"
                    value={settings.escalationDaysOverdue}
                    onChange={(e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => updateSetting('escalationDaysOverdue', parseInt(e.target.value))}
                    min={1}
                    max={30}
                  />
                  <p className="text-sm text-muted-foreground">
                    Number of days overdue before escalating to management
                  </p>
                </div>
              </>
            )}
          </CardContent>
        </Card>

        {/* Compliance */}
        <Card>
          <CardHeader>
            <div className="flex items-center gap-2">
              <Shield className="h-5 w-5" />
              <CardTitle>Compliance Gate</CardTitle>
            </div>
            <CardDescription>
              Control check-out based on compliance status
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="flex items-center justify-between">
              <div className="space-y-0.5">
                <Label>Enable Compliance Gate</Label>
                <p className="text-sm text-muted-foreground">
                  Check compliance before allowing check-out
                </p>
              </div>
              <Switch
                checked={settings.complianceGateEnabled}
                onCheckedChange={(checked) => updateSetting('complianceGateEnabled', checked)}
              />
            </div>

            {settings.complianceGateEnabled && (
              <>
                <Separator />
                <div className="space-y-4">
                  <div className="space-y-2">
                    <Label>Compliance Gate Action</Label>
                    <Select
                      value={settings.complianceGateAction}
                      onValueChange={(value: any) => updateSetting('complianceGateAction', value)}
                    >
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="allow">Allow (No restriction)</SelectItem>
                        <SelectItem value="warn">Warn (Show warning but allow)</SelectItem>
                        <SelectItem value="block">Block (Prevent check-out)</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>

                  <div className="flex items-center justify-between">
                    <div className="space-y-0.5">
                      <Label>Block Non-Compliant Check-Out</Label>
                      <p className="text-sm text-muted-foreground">
                        Prevent check-out of non-compliant assets
                      </p>
                    </div>
                    <Switch
                      checked={settings.blockNonCompliantCheckOut}
                      onCheckedChange={(checked) => updateSetting('blockNonCompliantCheckOut', checked)}
                    />
                  </div>
                </div>
              </>
            )}
          </CardContent>
        </Card>

        {/* Defaults */}
        <Card>
          <CardHeader>
            <CardTitle>Default Settings</CardTitle>
            <CardDescription>
              Set default values for check-out operations
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid gap-4 md:grid-cols-2">
              <div className="space-y-2">
                <Label>Default Check-Out Duration (Days)</Label>
                <Input
                  type="number"
                  value={settings.defaultCheckOutDays}
                  onChange={(e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => updateSetting('defaultCheckOutDays', parseInt(e.target.value))}
                  min={1}
                  max={365}
                />
              </div>
            </div>

            <Separator />

            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <div className="space-y-0.5">
                  <Label>Require Accessory Check</Label>
                  <p className="text-sm text-muted-foreground">
                    Mandate accessory verification during check-out/in
                  </p>
                </div>
                <Switch
                  checked={settings.requireAccessoryCheck}
                  onCheckedChange={(checked) => updateSetting('requireAccessoryCheck', checked)}
                />
              </div>

              <div className="flex items-center justify-between">
                <div className="space-y-0.5">
                  <Label>Require Photos</Label>
                  <p className="text-sm text-muted-foreground">
                    Require photo evidence during check-out/in
                  </p>
                </div>
                <Switch
                  checked={settings.requirePhotos}
                  onCheckedChange={(checked) => updateSetting('requirePhotos', checked)}
                />
              </div>

              <div className="flex items-center justify-between">
                <div className="space-y-0.5">
                  <Label>Require Condition Report</Label>
                  <p className="text-sm text-muted-foreground">
                    Mandate condition assessment during check-in
                  </p>
                </div>
                <Switch
                  checked={settings.requireConditionReport}
                  onCheckedChange={(checked) => updateSetting('requireConditionReport', checked)}
                />
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
      </div>
    </div>
  );
}
